import { incApi } from 'api/inc-api';
import {
  AchLocationType,
  AchLocationsType,
  ACHTransactionsType,
  ACHTransfersConfigType,
  BeneficialOwnersType,
} from './giftcard-ach-service.types';
import { getACHPoolLocationId } from './giftcard-ach-service.constants';
import { getMerchantId } from 'util/merchant-id';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';

const apiPrefix = 'incentivio-giftcard-ach-service';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    listAchLocations: build.query<AchLocationsType, void>({
      query: () => ({
        url: `${apiPrefix}/achlocations`,
      }),
      providesTags: generateProvidesList(
        result => result,
        element => element.locationId,
        'ACHLocation',
      ),
    }),

    getAchLocation: build.query<AchLocationType, { locationId: string }>({
      query: ({ locationId }) => ({
        url: `${apiPrefix}/achlocations/${locationId}`,
        params: { 'sync-status': true },
      }),
      keepUnusedDataFor: 0,
      async onQueryStarted(
        { locationId },
        { dispatch, getState, queryFulfilled },
      ) {
        const { data: location } = await queryFulfilled;
        const cachedLocation = endpoints.endpoints.listAchLocations
          .select()(getState())
          .data?.find(loc => loc.locationId === locationId);

        if (
          cachedLocation &&
          cachedLocation.overallStatus !== location.overallStatus
        ) {
          dispatch(
            endpoints.util.updateQueryData(
              'listAchLocations',
              undefined,
              draft => {
                draft.find(l => l.locationId === locationId).overallStatus =
                  location.overallStatus;
              },
            ),
          );
        }
      },
      providesTags: generateProvides(
        (result, error, { locationId }) => locationId,
        'ACHLocation',
      ),
    }),

    listBeneficialOwners: build.query<BeneficialOwnersType, void>({
      query: () => ({
        url: `${apiPrefix}/achlocations/${getACHPoolLocationId()}/beneficialowners`,
        headers: {
          merchantid: null,
        },
      }),
    }),

    listACHTransactions: build.query<
      ACHTransactionsType,
      {
        locationId: string;
        startDate: string;
        endDate: string;
        status: string;
        page: number;
        count: number;
      }
    >({
      query: ({ locationId, page, status, ...rest }) => ({
        url: `${apiPrefix}/achlocations/${
          locationId === 'ALL' ? '*' : locationId
        }/transfers`,
        params: {
          pageIndex: page,
          status: status === 'ALL' ? undefined : status,
          ...rest,
        },
        headers: {
          merchantid: getMerchantId(),
        },
      }),
      transformResponse: (res: ACHTransactionsType) => {
        const { pageIndex, pageSize, totalRecordCount } = res;
        return {
          ...res,
          paging: {
            total: totalRecordCount,
            count: pageSize,
            totalPages: Math.ceil(totalRecordCount / pageSize),
            currentPage: pageIndex,
          },
        };
      },
    }),

    updateACHLocations: build.mutation<
      void,
      {
        acceptedTOSAndPP: boolean;
        externalId: string;
        externalUrl: string;
        locationId: string;
        locationName: string;
      }
    >({
      query: body => ({
        url: `${apiPrefix}/achlocations`,
        method: 'PUT',
        body,
        headers: {
          merchantid: getMerchantId(),
        },
      }),
      invalidatesTags: ['ACHLocations'],
    }),

    fetchClientToken: build.query<{ token: string }, any>({
      query: tokenRequest => ({
        url: `${apiPrefix}/client-token`,
        credentials: 'include',
        method: 'POST',
        body: {
          action: tokenRequest.action,
          _links: !!tokenRequest.links ? tokenRequest.links : undefined,
        },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'Dwolla-Drop-Ins-Library',
        },
      }),
    }),

    removeFundingSource: build.mutation<
      void,
      { locationId: string; removeFromAllLocation: boolean }
    >({
      query: ({ locationId, removeFromAllLocation }) => ({
        url: `${apiPrefix}/achlocations/${locationId}/funding-source`,
        method: 'DELETE',
        params: { removeFromAllLocation },
      }),
      invalidatesTags: generateInvalidates(
        params => params.locationId,
        'ACHLocation',
      ),
    }),

    removeACHLocation: build.mutation<
      void,
      { locationId: string; removeFromAllLocation: boolean }
    >({
      query: ({ locationId, removeFromAllLocation }) => ({
        url: `${apiPrefix}/achlocations/${locationId}/customer`,
        method: 'DELETE',
        params: { removeFromAllLocation },
      }),
      invalidatesTags: ['ACHLocations'],
    }),

    updateLocationsFundTransferConfig: build.mutation<
      ACHTransfersConfigType,
      { locationId: string; active: boolean }
    >({
      query: ({ locationId, active }) => ({
        url: `${apiPrefix}/fund-transfer-config/locations`,
        method: 'PATCH',
        body: {
          locationId,
          active,
        },
      }),
      invalidatesTags: ['ACHLocations'],
    }),

    updatePoolLocationFundTransferConfig: build.mutation<
      ACHTransfersConfigType,
      { active: boolean }
    >({
      query: ({ active }) => ({
        url: `${apiPrefix}/fund-transfer-config/pool-account`,
        method: 'PATCH',
        body: {
          active,
        },
      }),
      invalidatesTags: ['ACHLocations'],
    }),

    addFundingAccount: build.mutation<
      ACHTransfersConfigType,
      { externalId: string; externalUrl: string; locationId: string }
    >({
      query: ({ externalId, externalUrl, locationId }) => ({
        url: `${apiPrefix}/achlocations/${locationId}/fundingaccount`,
        method: 'PUT',
        body: {
          externalId,
          externalUrl,
        },
        headers: {
          merchantid: getMerchantId(),
        },
      }),
      invalidatesTags: ['ACHLocations'],
    }),

    copyACHLocation: build.mutation<
      void,
      { fromLocationId: string; toLocationId: string }
    >({
      query: ({ fromLocationId, toLocationId }) => ({
        url: `${apiPrefix}/achlocations/copy-customer-and-account`,
        method: 'POST',
        body: {
          fromLocationId,
          toLocationIds: [toLocationId],
        },
      }),
      invalidatesTags: ['ACHLocations'],
    }),

    verifyMicroDeposits: build.mutation<
      void,
      {
        amount1: string;
        amount2: string;
        locationId: string;
        fundingAccountId: string;
      }
    >({
      query: ({ fundingAccountId, locationId, amount1, amount2 }) => ({
        url: `${apiPrefix}/achlocations/${locationId}/fundingaccount/${fundingAccountId}/verifymicrodeposits`,
        method: 'POST',
        body: {
          amount1,
          amount2,
        },
        headers: {
          merchantid: getMerchantId(),
        },
      }),
      invalidatesTags: generateInvalidates(
        params => params.locationId,
        'ACHLocation',
      ),
    }),
  }),
});

export const {
  useListAchLocationsQuery,
  useGetAchLocationQuery,
  useListBeneficialOwnersQuery,
  useListACHTransactionsQuery,
  useLazyFetchClientTokenQuery,
  useRemoveFundingSourceMutation,
  useRemoveACHLocationMutation,
  useUpdateACHLocationsMutation,
  useUpdateLocationsFundTransferConfigMutation,
  useUpdatePoolLocationFundTransferConfigMutation,
  useAddFundingAccountMutation,
  useCopyACHLocationMutation,
  useVerifyMicroDepositsMutation,
} = endpoints;
